import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Button, Search } from '@pmi/dsm-react';
import Overlay from '../../../../shared/components/Overlay';
import { SortedColumn } from '../../../../shared/components/ResponsiveTable/models';
import { useGetInternalAdminsQuery } from './apiSlice';
import {
  BaseInternalAdmin,
  emptyInternalAdmin,
  InternalAdminModel,
} from './models';
import styles from '../../../../shared/dsm.module.scss';
import InviteEditInternalAdminModal from './InviteEditInternalAdminModal';
import RemoveInternalAdminModal from './RemoveInternalAdminModal';
import { AdminActions, AdministratorUserApiModel } from '../AdminsTab/models';
import stylInternalAdmin from './internalAdmins.module.scss';
import InternalAdminList from './InternalAdminList';
import PeopleCardList from '../PeopleCardList';
import usePermissions from '../../../../shared/hooks/usePermissions';
import Permissions from '../../../../shared/permissions';
import InternalAdminCardListTemplate from './InternalAdminCardListTemplate';
import useBreakpoint, { SIZE_XS } from '../../../../shared/hooks/useBreakpoint';

function sortInternalAdminTableData(
  data: AdministratorUserApiModel[],
  fieldName: string,
  sortOrder: number,
) {
  const clone = [...data];

  if (fieldName === 'role') {
    return clone?.sort((a, b) => {
      const aRole = a?.accessType;
      const bRole = b?.accessType;

      // eslint-disable-next-line no-nested-ternary
      return aRole === bRole ? 0 : aRole > bRole ? sortOrder : -sortOrder;
    });
  }
  if (fieldName === 'dateRegistered') {
    const s = sortOrder === 1 ? 'z-pending' : '';
    return clone?.sort((a, b) => {
      const aD = a.personId > 0 ? a.acceptedDate : s;
      const bD = b.personId > 0 ? b.acceptedDate : s;
      // eslint-disable-next-line no-nested-ternary
      return aD === bD ? 0 : aD > bD ? sortOrder : -sortOrder;
    });
  }
  if (fieldName === 'invitedByPersonName') {
    const s = sortOrder === 1 ? 'z-invite-dby' : '';
    return clone?.sort((a, b) => {
      const aD = a?.invitedByPersonName
        ? a.invitedByPersonName?.toLowerCase()
        : s;
      const bD = b?.invitedByPersonName
        ? b.invitedByPersonName?.toLowerCase()
        : s;
      // eslint-disable-next-line no-nested-ternary
      return aD === bD ? 0 : aD > bD ? sortOrder : -sortOrder;
    });
  }
  if (fieldName === 'lastLoggedIn') {
    const s = sortOrder === 1 ? 'z-no-date' : '';
    return clone?.sort((a, b) => {
      const aD = a.personId > 0 ? a.lastLoggedIn ?? s : s;
      const bD = b.personId > 0 ? b?.lastLoggedIn ?? s : s;
      // eslint-disable-next-line no-nested-ternary
      return aD === bD ? 0 : aD > bD ? sortOrder : -sortOrder;
    });
  }
  if (fieldName === 'name') {
    const s = sortOrder === 1 ? 'z-no-name' : '';
    return clone?.sort((a, b) => {
      const aName = a.name?.toString().toLowerCase() || s;
      const bName = b.name?.toString().toLowerCase() || s;
      // eslint-disable-next-line no-nested-ternary
      return aName === bName ? 0 : aName > bName ? sortOrder : -sortOrder;
    });
  }

  return clone?.sort((a, b) =>
    // eslint-disable-next-line no-nested-ternary
    a[fieldName]?.toString().toLowerCase() ===
    b[fieldName]?.toString().toLowerCase()
      ? 0
      : a[fieldName]?.toString().toLowerCase() >
        b[fieldName]?.toString().toLowerCase()
      ? sortOrder
      : -sortOrder,
  );
}

export default function InternalAdminsTab() {
  const [editableAdmin, setEditableAdmin] = useState<
    BaseInternalAdmin | undefined
  >();
  const [removableAdmin, setRemovableAdmin] = useState<
    BaseInternalAdmin | undefined
  >();
  const [sortedColumn, setSortedColumn] = useState<SortedColumn>(null);
  const [searchText, setSearchText] = useState('');
  const [actions, setActions] = useState<AdminActions>({
    onEdit: setEditableAdmin,
    onRemove: setRemovableAdmin,
    removableAdmin,
  });
  const [searchResults, setSearchResult] = useState([]);
  const { hasAccess } = usePermissions();
  const bp = useBreakpoint();

  // todo - this is sorting demo, do this API end instead
  const {
    data: rawAdminList,
    isLoading: loading,
    isFetching: fetching,
    refetch,
  } = useGetInternalAdminsQuery(null);

  const adminList = useMemo(() => {
    if (loading || !rawAdminList) return rawAdminList?.slice();
    if (sortedColumn?.direction) {
      const sortOrder = sortedColumn.direction === 'Asc' ? 1 : -1;
      return sortInternalAdminTableData(
        rawAdminList,
        sortedColumn.name,
        sortOrder,
      );
    }
    return rawAdminList;
  }, [rawAdminList, loading, sortedColumn]);

  const editableAdminFromList = editableAdmin
    ? adminList?.find((a: AdministratorUserApiModel) => {
        if (editableAdmin.personId > 0)
          return a.personId === editableAdmin.personId;
        if (editableAdmin.inviteCode)
          return a.inviteCode === editableAdmin.inviteCode;
        return a.email === editableAdmin.email;
      })
    : null;

  const handleSort = (column: SortedColumn) => {
    setSortedColumn(column);
  };
  const resetSearch = () => {
    setSearchText('');
    setSearchResult([]);
  };

  const searchAdmin = (e: FormEvent<HTMLInputElement>) => {
    const el = e.target as HTMLInputElement;
    const text = el.value.toString().trim();
    if (text.length === 0) {
      setSearchText('');
      setSearchResult(null);
      return;
    }
    const result = adminList.filter(
      (admin: AdministratorUserApiModel) =>
        admin.invitationEmail
          ?.toString()
          .toLowerCase()
          .includes(text.toLowerCase()) ||
        admin.name?.toString().toLowerCase().includes(text.toLowerCase()) ||
        admin.personId?.toString() === text,
    );
    setSearchText(text);
    setSearchResult(result);
  };

  const cardTemplate = (item: AdministratorUserApiModel) => (
    <InternalAdminCardListTemplate
      actions={actions}
      item={item}
      isDeveloper={hasAccess({
        permissions: [Permissions.ManagePMIDeveloper],
      })}
    />
  );

  const renderTable = () => {
    if (loading) {
      return <p className="py-2">Loading...</p>;
    }
    const list = searchText?.length > 0 ? searchResults : adminList;
    const isSearchMode = list?.length === 0 && searchText?.length > 0;
    return (
      <>
        <InternalAdminList
          list={list}
          isLoading={loading}
          handleSort={handleSort}
          actions={actions}
          isDeveloper={hasAccess({
            permissions: [Permissions.ManagePMIDeveloper],
          })}
        />
        {isSearchMode && (
          <div
            className={`d-block my-5 text-center mx-auto w-75 ${stylInternalAdmin.noResult}`}
          >
            There are no search results matching{' '}
            <span data-exclude-lg>
              &ldquo;<b>{searchText}</b>&rdquo;.
            </span>
            <br />
            Please check your spelling or try another search.
          </div>
        )}
      </>
    );
  };
  const renderCardList = () => {
    const list = searchText.length > 0 ? searchResults : adminList;
    const isSearchMode = list?.length === 0 && searchText.length > 0;
    return (
      <>
        <PeopleCardList<InternalAdminModel>
          cardTemplate={cardTemplate}
          items={list}
          loading={loading}
          paginationProps={{ showPagination: false }}
        />
        {isSearchMode && (
          <div
            className={`d-block my-5 text-center mx-auto w-75 ${stylInternalAdmin.noResult}`}
          >
            There are no search results matching{' '}
            <span data-exclude-lg>
              &ldquo;<b>{searchText}</b> &rdquo;.
            </span>
            <br />
            Please check your spelling or try another search.
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    // forcing the text on invite button as DSM button keeps setting it to proper case
    document.querySelector('#btn-invite-wrapper > button').innerHTML =
      'Invite PMI Admin';
  }, []);

  useEffect(() => {
    setActions({ ...actions, removableAdmin });
  }, [removableAdmin]);

  return (
    <div className="d-block">
      <div className="d-flex flex-column flex-sm-row mb-3 w-100">
        <Search
          id="internalAdminSearch"
          placeholder="Search by name or email address"
          className={`${styles.secondarySearch} me-0 me-sm-3`}
          onChange={(e) => searchAdmin(e)}
          onClear={resetSearch}
          value={searchText}
          style={{ width: 330 }}
        />
        <div className="d-flex w-100 mt-2 mt-sm-0">
          <div className="flex-grow-1 text-end" id="btn-invite-wrapper">
            <Button
              titleText="Invite PMI Admin"
              size="md"
              type="button"
              className={stylInternalAdmin.btnInvite}
              onClick={() => setEditableAdmin(emptyInternalAdmin)}
            />
          </div>
        </div>
      </div>
      {bp !== SIZE_XS && renderTable()}
      {bp === SIZE_XS && renderCardList()}
      {editableAdmin && (
        <InviteEditInternalAdminModal
          admin={{
            ...editableAdmin,
            phone: editableAdminFromList?.phone,
            phoneNumber: editableAdminFromList?.phoneNumber,
          }}
          onSuccess={() => {
            setEditableAdmin(undefined);
            refetch();
          }}
          onCancel={() => setEditableAdmin(undefined)}
        />
      )}
      {removableAdmin && (
        <RemoveInternalAdminModal
          admin={removableAdmin}
          onCancel={() => setRemovableAdmin(undefined)}
        />
      )}
      <Overlay
        variant="PMIOverlay"
        global
        animated
        fade
        iconColor="brand-dark"
        overlayColor="black"
        overlayOpacity={1}
        show={loading || fetching}
      />
    </div>
  );
}
